import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import BookIcon from "../../../assets/Dashboard/icons/BookIcon";
import ReportIcon from "../../../assets/Dashboard/icons/ReportIcon";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../components/common/BriaButtonGroup/BriaButtonGroup";
import LinkConstants from "../../../constants/LinkConstants";
import { useAttributionReports } from "../../../hooks/useAttributionReports";
import { useAppStore } from "../../../hooks/useStores";
import DashboardWidget from "../Widget/DashboardWidget";
import styles from "./AttributionWidget.module.scss";
import AttributionWidgetStats from "./Chart/AttributionWidgetStats";

type Props = {
	className?: string;
};

const AttributionWidget = ({ className }: Props) => {
	const { t } = useTranslation("dashboard", { keyPrefix: "attribution" });
	const { appStore, attributionReportsStore } = useAppStore();

	const now = new Date();
	const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
	const today = new Date(now.setHours(23, 59, 59, 999));

	const startDate = firstDayOfMonth.toLocaleDateString();
	const endDate = today.toLocaleDateString();
	const { exportReport } = useAttributionReports();
	const LEGAL_LOBBY_LINK = `${LinkConstants.LEGAL_LOBBY}?_gl=1*12vtyg8*_gcl_au*MzQyMzUxMzAxLjE3NDcwNDU4NTg.*_ga*MjAxNDkyNjc3Ni4xNzA3ODI0MjY4*_ga_WRN60H46X4*czE3NTI0OTE0NzgkbzIyMCRnMSR0MTc1MjQ5Mjg3MCRqNTckbDAkaDA.`;

	useEffect(() => {
		appStore.verifyAgentInstallation();
	}, []);

	const handleReportClick = () => {
		exportReport("Catalog Report", `monthly_attribution_report_${new Date().toLocaleDateString()}`);
	};

	return (
		<DashboardWidget className={clsx(styles.widget, className)} title={t("title")}>
			<AttributionWidgetStats startDate={startDate} endDate={endDate} />
			<BriaButtonGroup className={styles.buttons}>
				<BriaButton
					buttonType="textSmall"
					className={styles.textButton}
					startIcon={<ReportIcon />}
					onClick={handleReportClick}
					disabled={!attributionReportsStore.catalogReportData.length}
				>
					{t("reportBtn")}
				</BriaButton>
				<BriaButton
					buttonType="textSmall"
					className={styles.textButton}
					startIcon={<BookIcon />}
					onClick={() => window.open(LEGAL_LOBBY_LINK, "_blank")}
				>
					{t("legalBtn")}
				</BriaButton>
				{(appStore.isVerifyingAgentInstallation || !appStore.agentInstallation) && (
					<BriaButton
						buttonType="primaryMedium"
						className={styles.primaryButton}
						loading={appStore.isVerifyingAgentInstallation}
						disabled={appStore.isVerifyingAgentInstallation}
						onClick={() => window.open(LinkConstants.BRIA_AGENT, "_blank")}
					>
						{t("agentBtn")}
					</BriaButton>
				)}
			</BriaButtonGroup>
		</DashboardWidget>
	);
};

export default observer(AttributionWidget);
